import axios from 'axios';
import { Cookies } from 'react-cookie';
import { getToken, getScenarioId, setToken, clearToken } from '@/Config/auth/authStore';

export const readCsrfToken = (): string | undefined => new Cookies().get('csrftoken');

// Read once for raw axios calls; it goes stale if the cookie rotates, so prefer readCsrfToken().
const initialCsrfToken = readCsrfToken();
if (initialCsrfToken) {
  axios.defaults.headers.common['X-CSRFToken'] = initialCsrfToken;
}

export const httpClient = axios.create({});

// --- Request interceptor ---
// Reads JWT and CSRF cookie *per request* so Django's csrf token rotation on
// session login (and stale module-load reads on first paint) don't desync the
// X-CSRFToken header from the cookie Django will compare it against.
httpClient.interceptors.request.use((config) => {
  const jwt = getToken();
  if (jwt) {
    config.headers.Authorization = `Bearer ${jwt}`;
  }

  const scenarioId = getScenarioId();
  if (scenarioId) {
    config.headers['scenario-id'] = scenarioId;
  }

  const csrfToken = readCsrfToken();
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken;
  }

  return config;
});

// --- Response interceptor ---
// On 401, try a Bearer refresh first. If that fails because the JWT is
// genuinely expired/revoked (its own 401), fall back to a session-based
// refresh — the Django session cookie usually outlives the JWT. Only
// clear auth state when both refresh paths fail with auth errors;
// transient 5xx/network failures keep the existing token intact so a
// blip on the refresh endpoint doesn't log the user out.
let refreshPromise: Promise<string | null> | null = null;
const REFRESH_ENDPOINT = '/v1/api/auth/refresh_jwt';
const SESSION_REFRESH_ENDPOINT = '/v1/api/auth/generate_jwt';
const SESSION_REFRESH_TIMEOUT_MS = 8000;

const isAuthError = (err: unknown): boolean => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 403;
};

httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as typeof error.config & { skipAuthRefresh?: boolean };

    // Only attempt refresh once per request and only for 401s. Both
    // refresh endpoints are short-circuited so a refresh that 401s
    // never re-enters this interceptor.
    if (
      error.response?.status !== 401 ||
      originalRequest.skipAuthRefresh ||
      originalRequest._retried ||
      originalRequest.url === REFRESH_ENDPOINT ||
      originalRequest.url === SESSION_REFRESH_ENDPOINT
    ) {
      return Promise.reject(error);
    }

    originalRequest._retried = true;

    // Coalesce concurrent refresh attempts into one request.
    if (!refreshPromise) {
      refreshPromise = (async () => {
        const currentToken = getToken();
        if (currentToken) {
          try {
            const response = await axios.post<{ token: string }>(REFRESH_ENDPOINT, null, {
              headers: { Authorization: `Bearer ${currentToken}` },
            });
            const newToken = response.data.token;
            setToken(newToken);
            return newToken;
          } catch {
            // Fall through to the session path regardless of why the Bearer
            // refresh failed — the session cookie can still mint a fresh JWT,
            // and the session path below only wipes auth state on a
            // definitive 401/403, so a transient blip stays harmless.
          }
        }

        // Bearer refresh rejected (or no token at all): try minting a
        // fresh JWT from the still-valid Django session cookie. This
        // is what saves a user whose JWT just expired but who still
        // has a healthy session.
        try {
          const response = await axios.get<{ token: string }>(SESSION_REFRESH_ENDPOINT, {
            withCredentials: true,
            timeout: SESSION_REFRESH_TIMEOUT_MS,
          });
          const newToken = response.data.token;
          setToken(newToken);
          return newToken;
        } catch (sessionErr) {
          if (isAuthError(sessionErr)) {
            clearToken();
          }
          return null;
        }
      })().finally(() => {
        refreshPromise = null;
      });
    }

    const newToken = await refreshPromise;
    if (!newToken) {
      return Promise.reject(error);
    }

    return httpClient(originalRequest);
  },
);

/**
 * Drop any in-flight coalesced refresh. Called on logout so a subsequent
 * session (in the rare path where the page does not fully reload) never awaits
 * a promise tied to the previous session's token.
 */
export function resetRefreshState(): void {
  refreshPromise = null;
}

export default axios;
